import React, { useMemo, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';

import Select from '../../UI/Select';
import Vendor from '../../../data/Vendor';
import { getItems } from '../../../lib/SessionStorage';
import { capitalize, capitalizeFirstLetter } from '../../../utils/formatters';
import { Background, Main } from '../../../styles/themeColors';
import { Size } from '../../../lib/styles';
import { useAPI } from '../../../lib/API';
import { getTopProductBrands } from '../../../requests/product';
import MatTableColumnsButton from '../../MaterialTable/MatTableColumnsButton';
import ProductColumns from './ProductListingColumns';
import { save } from '../../../lib/LocalStorage';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import ProductLocalization from '../ProductLocalization';
import Brand from '../../../data/Brand';
import { PRODUCT_COLUMNS_STORAGE_KEY } from 'constants/product';
import { STORAGE_KEYS, GENERAL_CONSTANTS } from 'constants/general';

const FilterBackground = muiStyled(Grid)({
  background: '#F0F1F4',
  padding: '10px 20px',
});

const TableMarginGrid = muiStyled(Grid)({
  marginTop: '24px',
});

const ColumnSettingsGrid = muiStyled(Grid)({
  alignContent: 'center',
  justifyContent: 'flex-end',
  display: 'grid',
});
interface Props {
  selectedVendorId?: number;
  vendorsList: Vendor[];
  filters: ProductListFilter;
  filterUpdate: (filters: ProductListFilter) => void;
  updateColumnSettings: Function;
  productColumns: Column<any>[];
  loading: boolean;
  selectedBrands?: Brand[];
  isBrandUser?: boolean;
  hideBrandFilter?: boolean;
}

interface SelectedOption {
  value: string;
  id?: string;
  code?: string;
}

export interface ProductListFilter {
  publishStatus: SelectedOption;
  brands: SelectedOption[];
  vendor: SelectedOption;
  country: SelectedOption;
}

const ProductListingFilters = ({
  productColumns,
  selectedVendorId,
  vendorsList,
  filters,
  filterUpdate,
  updateColumnSettings,
  loading,
  selectedBrands,
  hideBrandFilter
}: Props) => {

  // Fetch brands and countries from session storage
  const brands = getItems(STORAGE_KEYS.BRANDS);
  const productStatuses = getItems(STORAGE_KEYS.PRODUCT_STATUSES);
  const countries = getItems(STORAGE_KEYS.COUNTRIES);

  // Memoize brand list to avoid recalculation
  const brandList = useMemo(() => brands, [brands]);

  // Memoize selected brand names
  const selectedBrandNames = useMemo(() => {
    return filters.brands.map(brand => brand.value);
  }, [filters.brands]);

  // Callback for publish status change
  const handlePublishStatusChange = useCallback((event: any) => {
    filterUpdate({
      ...filters,
      publishStatus: {
        value: event.target.value,
        id: event.target.id,
        code: event.target.code
      },
    });
  }, [filters, filterUpdate]);

  // Callback for brand change
  const handleBrandChange = useCallback((event: any) => {
    const selBrands = event.target.value;
    const selectedItem = event.target.selected;

    const isAdding = selBrands.indexOf(selectedItem) > -1;
    
    const updatedBrands = isAdding
      ? [...filters.brands, { value: selectedItem, id: event.target.id }]
      : filters.brands.filter((brand) => brand.value !== selectedItem);

    filterUpdate({
      ...filters,
      brands: updatedBrands,
    });
  }, [filters, filterUpdate]);

  // Callback for vendor change
  const handleVendorChange = useCallback((event: any) => {
    filterUpdate({
      ...filters,
      vendor: { value: event.target.value, id: event.target.id },
    });
  }, [filters, filterUpdate]);

  // Callback for country change
  const handleCountryChange = useCallback((event: any) => {
    filterUpdate({
      ...filters,
      country: { value: event.target.value, id: event.target.id },
    });
  }, [filters, filterUpdate]);

  return (
    <>
      <TableMarginGrid size={12}>
        <FilterBackground container spacing={1}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Select
              label="Publish Status"
              disabled={loading}
              labelPosition="top"
              options={
                productStatuses
                  ? productStatuses.map((status: any) => ({
                    value: capitalize(status.name),
                    code: status.name,
                    id: status.id,
                  }))
                  : []
              }
              onChange={handlePublishStatusChange}
              value={filters.publishStatus.value || GENERAL_CONSTANTS.ALL}
              defaultValue={GENERAL_CONSTANTS.ALL}
              width="100%"
              displayEmpty={GENERAL_CONSTANTS.ALL}
            />
          </Grid>

          {!hideBrandFilter && <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Select
              label="Brand"
              disabled={loading || !!selectedBrands?.length}
              labelPosition="top"
              options={
                brandList.map((brand: any) => ({
                  value: brand.name,
                  id: brand.id,
                })) || []
              }
              onChange={handleBrandChange}
              value={selectedBrandNames}
              defaultValue={ProductLocalization.listing.filter.brandSelect}
              width="100%"
              multiple={true}
              limitTags={1}
            />
          </Grid>}

          {!selectedVendorId && vendorsList && <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Select
              label="Vendor"
              labelPosition="top"
              disabled={loading}
              options={vendorsList.map((vendor: Vendor) => ({
                value: vendor.name,
                id: vendor.id + '',
              }))}
              onChange={handleVendorChange}
              value={filters.vendor.value}
              defaultValue={ProductLocalization.listing.filter.vendorSelect}
              width="100%"
              displayEmpty={ProductLocalization.listing.filter.vendorSelect}
            />
          </Grid>}

          <Grid size={{ xs: 12, sm: 6, md: selectedVendorId ? 4 : 2 }}>
            <Select
              label="Country"
              disabled={loading}
              labelPosition="top"
              options={countries.map((country: any) => ({
                value: country.countryName,
                id: country.countryIso,
              }))}
              onChange={handleCountryChange}
              value={filters.country.value}
              defaultValue={ProductLocalization.listing.filter.countrySelect}
              width="100%"
              displayEmpty={ProductLocalization.listing.filter.countrySelect}
            />
          </Grid>

          <ColumnSettingsGrid size={{ xs: 12, sm: 6, md: 2 }}>
            <MatTableColumnsButton
              columns={productColumns}
              onUpdateColumns={(columns) => {
                save(PRODUCT_COLUMNS_STORAGE_KEY, columns);
                updateColumnSettings(columns);
              }}
              initialColumns={ProductColumns} showButton />
          </ColumnSettingsGrid>
        </FilterBackground>
      </TableMarginGrid>
    </>
  );
};

export default ProductListingFilters;
